import { Component, OnInit, ViewChild, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { CustomTitle, LoadingPanelService, FormGroupErrorMessageComponent, Utils } from 'lib-base';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IGetPerfilUsuario, IEditarNucleoFamiliar, INucleoFamiliarConsulta, IGeneroOSexoConsulta, IVinculoConsulta, IGruposDeClasificacionConsulta } from '../../../shared/interfaces/sisben/sisben';
import { ITipoIdentificacion } from '../../../shared/interfaces/database/seguridad';
import { SisbenNucleoFamiliarService } from '../../../services/sisben/sisben-nucleo-familiar.service';
import { GeneroOSexoService } from '../../../services/sisben/genero-o-sexo.service';
import { VinculoService } from '../../../services/sisben/vinculo.service';
import { GruposClasificacionService } from '../../../services/sisben/grupos-clasificacion.service';
import { TipoIdentificacionService } from '../../../services/database/tipoIdentificacion.service';
import { MessageService } from 'primeng/api';
import { firstValueFrom, forkJoin } from 'rxjs';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { InputTextareaModule } from 'primeng/inputtextarea';

@Component({
  selector: 'app-sisben-gestion-formulario',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, DialogModule, ButtonModule, CustomTitle, TranslateModule,
    FormGroupErrorMessageComponent, InputTextModule, InputNumberModule, DropdownModule,
    CalendarModule, InputTextareaModule
  ],
  template: `
    <form [formGroup]="formulario" (ngSubmit)="onSubmit()">
      <p-dialog [(visible)]="displayModal" [modal]="true" 
          [autoZIndex]="true" [draggable]="false" [resizable]="true" [maximizable]="true" 
          [breakpoints]="{'640px': '100vw'}" [style]="{width: '80vw'}">

        <ng-template pTemplate="header">
          <CustomTitle [title]="formTitle" />
        </ng-template>

        <ng-template pTemplate="content">
          <main class="grid formgrid p-fluid">
            <!-- Primer Nombre -->
            <div class="field col-12 md:col-6">
              <label for="primerNombre">{{ 'common.primerNombre' | translate }}<span class="text-red-500">*</span></label>
              <input pInputText id="primerNombre" formControlName="primerNombre" type="text" />
              <app-form-group-error-message *ngIf="formulario.get('primerNombre')?.invalid && formulario.get('primerNombre')?.touched"
                [control]="formulario.get('primerNombre')"></app-form-group-error-message>
            </div>

            <!-- Segundo Nombre -->
            <div class="field col-12 md:col-6">
              <label for="segundoNombre">{{ 'common.segundoNombre' | translate }}</label>
              <input pInputText id="segundoNombre" formControlName="segundoNombre" type="text" />
              <app-form-group-error-message *ngIf="formulario.get('segundoNombre')?.invalid && formulario.get('segundoNombre')?.touched"
                [control]="formulario.get('segundoNombre')"></app-form-group-error-message>
            </div>

            <!-- Primer Apellido -->
            <div class="field col-12 md:col-6">
              <label for="primerApellido">{{ 'common.primerApellido' | translate }}<span class="text-red-500">*</span></label>
              <input pInputText id="primerApellido" formControlName="primerApellido" type="text" />
              <app-form-group-error-message *ngIf="formulario.get('primerApellido')?.invalid && formulario.get('primerApellido')?.touched"
                [control]="formulario.get('primerApellido')"></app-form-group-error-message>
            </div>

            <!-- Segundo Apellido -->
            <div class="field col-12 md:col-6">
              <label for="segundoApellido">{{ 'common.segundoApellido' | translate }}</label>
              <input pInputText id="segundoApellido" formControlName="segundoApellido" type="text" />
              <app-form-group-error-message *ngIf="formulario.get('segundoApellido')?.invalid && formulario.get('segundoApellido')?.touched"
                [control]="formulario.get('segundoApellido')"></app-form-group-error-message>
            </div>

            <!-- Tipo de Identificación -->
            <div class="field col-12 md:col-6">
              <label for="tipoIdentificacion">{{ 'common.tipoIdentificacion' | translate }}<span class="text-red-500">*</span></label>
              <p-dropdown id="tipoIdentificacion" [options]="tiposIdentificacion"
                formControlName="tipoIdentificacion" optionValue="id"
                optionLabel="descripcion" [showClear]="true"
                placeholder="Seleccione tipo de identificación"></p-dropdown>
              <app-form-group-error-message *ngIf="formulario.get('tipoIdentificacion')?.invalid && formulario.get('tipoIdentificacion')?.touched"
                [control]="formulario.get('tipoIdentificacion')"></app-form-group-error-message>
            </div>

            <!-- Identificación -->
            <div class="field col-12 md:col-6">
              <label for="identificacion">{{ 'common.identificacion' | translate }}<span class="text-red-500">*</span></label>
              <input pInputText id="identificacion" formControlName="identificacion" type="text" />
              <app-form-group-error-message *ngIf="formulario.get('identificacion')?.invalid && formulario.get('identificacion')?.touched"
                [control]="formulario.get('identificacion')"></app-form-group-error-message>
            </div>

            <!-- Fecha de Expedición -->
            <div class="field col-12 md:col-6">
              <label for="fechaExpedicion">{{ 'common.fechaExpedicion' | translate }}<span class="text-red-500">*</span></label>
              <p-calendar id="fechaExpedicion" formControlName="fechaExpedicion"
                [showIcon]="true" dateFormat="dd/mm/yy" [maxDate]="today">
              </p-calendar>
              <app-form-group-error-message *ngIf="formulario.get('fechaExpedicion')?.invalid && formulario.get('fechaExpedicion')?.touched"
                [control]="formulario.get('fechaExpedicion')"></app-form-group-error-message>
            </div>

            <!-- Lugar de Expedición -->
            <div class="field col-12 md:col-6">
              <label for="lugarExpedicion">{{ 'common.lugarExpedicion' | translate }}<span class="text-red-500">*</span></label>
              <input pInputText id="lugarExpedicion" formControlName="lugarExpedicion" type="text" />
              <app-form-group-error-message *ngIf="formulario.get('lugarExpedicion')?.invalid && formulario.get('lugarExpedicion')?.touched"
                [control]="formulario.get('lugarExpedicion')"></app-form-group-error-message>
            </div>

            <!-- Género -->
            <div class="field col-12 md:col-6">
              <label for="genero">{{ 'common.genero' | translate }}<span class="text-red-500">*</span></label>
              <p-dropdown id="genero" [options]="generos"
                formControlName="genero" optionValue="id"
                optionLabel="generoOSexo" [showClear]="true"
                placeholder="Seleccione género"></p-dropdown>
              <app-form-group-error-message *ngIf="formulario.get('genero')?.invalid && formulario.get('genero')?.touched"
                [control]="formulario.get('genero')"></app-form-group-error-message>
            </div>

            <!-- Fecha de Nacimiento -->
            <div class="field col-12 md:col-6">
              <label for="fechaNacimiento">{{ 'common.fechaNacimiento' | translate }}<span class="text-red-500">*</span></label>
              <p-calendar id="fechaNacimiento" formControlName="fechaNacimiento"
                [showIcon]="true" dateFormat="dd/mm/yy" [maxDate]="today">
              </p-calendar>
              <app-form-group-error-message *ngIf="formulario.get('fechaNacimiento')?.invalid && formulario.get('fechaNacimiento')?.touched"
                [control]="formulario.get('fechaNacimiento')"></app-form-group-error-message>
            </div>

            <!-- Barrio -->
            <div class="field col-12 md:col-6">
              <label for="barrio">{{ 'common.barrio' | translate }}</label>
              <input pInputText id="barrio" formControlName="barrio" type="text" />
              <app-form-group-error-message *ngIf="formulario.get('barrio')?.invalid && formulario.get('barrio')?.touched"
                [control]="formulario.get('barrio')"></app-form-group-error-message>
            </div>

            <!-- Dirección -->
            <div class="field col-12 md:col-6">
              <label for="direccion">{{ 'common.direccion' | translate }}</label>
              <input pInputText id="direccion" formControlName="direccion" type="text" />
              <app-form-group-error-message *ngIf="formulario.get('direccion')?.invalid && formulario.get('direccion')?.touched"
                [control]="formulario.get('direccion')"></app-form-group-error-message>
            </div>

            <!-- Correo -->
            <div class="field col-12 md:col-6">
              <label for="correo">{{ 'common.correo' | translate }}</label>
              <input pInputText id="correo" formControlName="correo" type="email" />
              <app-form-group-error-message *ngIf="formulario.get('correo')?.invalid && formulario.get('correo')?.touched"
                [control]="formulario.get('correo')"></app-form-group-error-message>
            </div>

            <!-- Teléfono Móvil -->
            <div class="field col-12 md:col-6">
              <label for="telefonoMovil">{{ 'common.telefonoMovil' | translate }}</label>
              <input pInputText id="telefonoMovil" formControlName="telefonoMovil" type="text" />
              <app-form-group-error-message *ngIf="formulario.get('telefonoMovil')?.invalid && formulario.get('telefonoMovil')?.touched"
                [control]="formulario.get('telefonoMovil')"></app-form-group-error-message>
            </div>

            <!-- Teléfono Fijo -->
            <div class="field col-12 md:col-6">
              <label for="telefonoFijo">{{ 'common.telefonoFijo' | translate }}</label>
              <input pInputText id="telefonoFijo" formControlName="telefonoFijo" type="text" />
              <app-form-group-error-message *ngIf="formulario.get('telefonoFijo')?.invalid && formulario.get('telefonoFijo')?.touched"
                [control]="formulario.get('telefonoFijo')"></app-form-group-error-message>
            </div>

            <!-- Jefe de Hogar -->
            <div class="field col-12 md:col-6">
              <label for="jefeHogar">{{ 'common.jefeHogar' | translate }}</label>
              <p-dropdown id="jefeHogar" [options]="jefesDeFamilia"
                formControlName="jefeHogar" optionValue="id"
                optionLabel="nombreCompleto" [showClear]="true"
                placeholder="Seleccione jefe de hogar"></p-dropdown>
              <app-form-group-error-message *ngIf="formulario.get('jefeHogar')?.invalid && formulario.get('jefeHogar')?.touched"
                [control]="formulario.get('jefeHogar')"></app-form-group-error-message>
            </div>

            <!-- Vínculo -->
            <div class="field col-12 md:col-6">
              <label for="vinculo">{{ 'common.vinculo' | translate }}</label>
              <p-dropdown id="vinculo" [options]="vinculos"
                formControlName="vinculo" optionValue="id"
                optionLabel="nombre" [showClear]="true"
                placeholder="Seleccione vínculo"></p-dropdown>
              <app-form-group-error-message *ngIf="formulario.get('vinculo')?.invalid && formulario.get('vinculo')?.touched"
                [control]="formulario.get('vinculo')"></app-form-group-error-message>
            </div>

            <!-- Puntaje SISBEN -->
            <div class="field col-12 md:col-6">
              <label for="puntajeSisben">{{ 'common.puntajeSisben' | translate }}</label>
              <p-dropdown id="puntajeSisben" [options]="puntajesSisben"
                formControlName="puntajeSisben" optionValue="id"
                optionLabel="puntaje" [showClear]="true"
                placeholder="Seleccione puntaje SISBEN"></p-dropdown>
              <app-form-group-error-message *ngIf="formulario.get('puntajeSisben')?.invalid && formulario.get('puntajeSisben')?.touched"
                [control]="formulario.get('puntajeSisben')"></app-form-group-error-message>
            </div>
          </main>
        </ng-template>
        
        <ng-template pTemplate="footer">
          <div class="field col-12">
            <div class="flex justify-content-between">
              <button pButton type="button" [label]="'form.button.limpiar' | translate" iconPos="right" icon="pi pi-eraser" class="tax-app-button"
                style="width: 16rem;" (click)="resetForm()"></button>
              <button pButton type="submit" [label]="'form.button.guardar' | translate" icon="pi pi-save" iconPos="right" class="tax-app-button"
                [disabled]="formulario.invalid"
                style="width: 16rem;"></button>
            </div>
          </div>
        </ng-template>
      </p-dialog>
    </form>
  `
})
export class SisbenGestionFormularioComponent implements OnInit {
  formTitle = '';
  displayModal = false;
  formulario!: FormGroup;
  perfilSeleccionado!: IGetPerfilUsuario;
  trans: any = {};
  today: Date = new Date();

  // Dropdowns
  tiposIdentificacion: ITipoIdentificacion[] = [];
  generos: IGeneroOSexoConsulta[] = [];
  vinculos: IVinculoConsulta[] = [];
  puntajesSisben: IGruposDeClasificacionConsulta[] = [];
  jefesDeFamilia: INucleoFamiliarConsulta[] = [];

  @Output() formularioGuardado = new EventEmitter<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly t: TranslateService,
    private readonly _snds: SisbenNucleoFamiliarService,
    private readonly _gos: GeneroOSexoService,
    private readonly _vs: VinculoService,
    private readonly _gcs: GruposClasificacionService,
    private readonly _tis: TipoIdentificacionService,
    private readonly _ms: MessageService,
    private readonly _lps: LoadingPanelService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.translate().then(() => {
      this.loadCatalogos();
    });
  }

  private async translate(): Promise<void> {
    this.trans = await firstValueFrom(forkJoin({
      editarRegistroSisben: this.t.get('msg.editarRegistroSisben'),
      exito: this.t.get('common.exito'),
      registroGuardado: this.t.get('msg.registroGuardado'),
      errorGuardando: this.t.get('msg.errorGuardando'),
      noCambiosFormulario: this.t.get('msg.noCambiosFormulario'),
      completeCamposRequeridos: this.t.get('msg.completeCamposRequeridos'),
      advertencia: this.t.get('common.advertencia')
    }));
  }

  private initializeForm(): void {
    this.formulario = this.fb.group({
      primerNombre: [null, [Validators.required, Validators.maxLength(255)]],
      segundoNombre: [null, [Validators.maxLength(255)]],
      primerApellido: [null, [Validators.required, Validators.maxLength(255)]],
      segundoApellido: [null, [Validators.maxLength(255)]],
      tipoIdentificacion: [null, [Validators.required]],
      identificacion: [null, [Validators.required, Validators.maxLength(255)]],
      fechaExpedicion: [null, [Validators.required]],
      lugarExpedicion: [null, [Validators.required, Validators.maxLength(255)]],
      genero: [null, [Validators.required]],
      fechaNacimiento: [null, [Validators.required]],
      barrio: [null, [Validators.maxLength(255)]],
      direccion: [null, [Validators.maxLength(255)]],
      correo: [null, [Validators.email, Validators.maxLength(255)]],
      telefonoMovil: [null, [Validators.maxLength(255)]],
      telefonoFijo: [null, [Validators.maxLength(255)]],
      jefeHogar: [null],
      vinculo: [null],
      puntajeSisben: [null]
    });
  }

  private loadCatalogos(): void {
    this._lps.show(true);
    forkJoin({
      tipos: this._tis.getTipoIdentificaciones(),
      generos: this._gos.findAll(),
      vinculos: this._vs.findAll(),
      puntajes: this._gcs.findAll(),
      jefes: this._snds.consultarNucleosFamiliares()
    }).subscribe({
      next: (data) => {
        this.tiposIdentificacion = data.tipos;
        this.generos = data.generos;
        this.vinculos = data.vinculos;
        this.puntajesSisben = data.puntajes;
        this.jefesDeFamilia = data.jefes;
        this._lps.hide();
      },
      error: () => {
        this._lps.hide();
        this._ms.add({ severity: 'error', summary: this.trans.advertencia, detail: 'Error cargando catálogos' });
      }
    });
  }

  show(data: IGetPerfilUsuario): void {
    this.perfilSeleccionado = data;
    this.formTitle = this.trans.editarRegistroSisben;
    this.cargarDatosEnFormulario(data);
    this.displayModal = true;
  }

  private cargarDatosEnFormulario(data: IGetPerfilUsuario): void {
    const fechaExpedicion = this.formatearFecha(data.fechaExpedicion);
    const fechaNacimiento = this.formatearFecha(data.fechaNacimiento);

    this.formulario.patchValue({
      primerNombre: data.primerNombre,
      segundoNombre: data.segundoNombre,
      primerApellido: data.primerApellido,
      segundoApellido: data.segundoApellido,
      tipoIdentificacion: data.tipoIdentificacionId,
      identificacion: data.identificacion,
      fechaExpedicion: fechaExpedicion,
      lugarExpedicion: data.lugarExpedicion,
      genero: data.generoOSexoId,
      fechaNacimiento: fechaNacimiento,
      barrio: data.barrio,
      direccion: data.direccion,
      correo: data.correo,
      telefonoMovil: data.telefonoMovil,
      telefonoFijo: data.telefonoFijo,
      jefeHogar: data.jefeHogarId,
      vinculo: data.vinculoId,
      puntajeSisben: data.puntajeSisbenId
    }, { emitEvent: false });
    
    this.formulario.markAsPristine();
  }

  private formatearFecha(fecha: string | Date): Date | null {
    if (!fecha) return null;
    if (fecha instanceof Date) return fecha;
    const date = new Date(fecha);
    return isNaN(date.getTime()) ? null : date;
  }

  onSubmit(): void {
    if (this.formulario.pristine) {
      this._ms.add({ severity: 'warn', summary: this.trans.advertencia, detail: this.trans.noCambiosFormulario });
      return;
    }

    if (this.formulario.invalid) {
      Utils.markAllFormControlsAsTouched(this.formulario);
      this._ms.add({ severity: 'warn', summary: this.trans.advertencia, detail: this.trans.completeCamposRequeridos });
      return;
    }

    this.guardarRegistro();
  }

  private guardarRegistro(): void {
    this._lps.show(true);
    const dto: IEditarNucleoFamiliar = this.prepararDto();

    this._snds.editarNucleoFamiliar(this.perfilSeleccionado.id, dto).subscribe({
      next: () => {
        this._lps.hide();
        this._ms.add({ severity: 'success', summary: this.trans.exito, detail: this.trans.registroGuardado });
        this.displayModal = false;
        this.formularioGuardado.emit();
      },
      error: (error) => {
        this._lps.hide();
        this._ms.add({ severity: 'error', summary: this.trans.advertencia, detail: this.trans.errorGuardando });
      }
    });
  }

  private prepararDto(): IEditarNucleoFamiliar {
    const formValues = this.formulario.value;
    return {
      primerNombre: formValues.primerNombre,
      segundoNombre: formValues.segundoNombre || undefined,
      primerApellido: formValues.primerApellido,
      segundoApellido: formValues.segundoApellido || undefined,
      tipoIdentificacion: formValues.tipoIdentificacion,
      identificacion: formValues.identificacion,
      fechaExpedicion: this.convertirFechaAString(formValues.fechaExpedicion),
      lugarExpedicion: formValues.lugarExpedicion,
      genero: formValues.genero,
      fechaNacimiento: this.convertirFechaAString(formValues.fechaNacimiento),
      barrio: formValues.barrio,
      direccion: formValues.direccion,
      correo: formValues.correo,
      telefonoMovil: formValues.telefonoMovil || undefined,
      telefonoFijo: formValues.telefonoFijo || undefined,
      jefeHogar: formValues.jefeHogar || undefined,
      vinculo: formValues.vinculo || undefined,
      puntajeSisben: formValues.puntajeSisben || undefined
    };
  }

  private convertirFechaAString(fecha: Date | string): string {
    if (!fecha) return '';
    if (typeof fecha === 'string') return fecha;
    return fecha.toISOString().split('T')[0]; // Formato YYYY-MM-DD
  }

  resetForm(): void {
    this.formulario.reset();
  }
}
